"use client"

import { useEffect } from "react"

export default function DevCssGuard() {
  // The development-only guard is INSIDE the effect, not in front of it
  // (FRIDGE-307). A hook must run on every render of a component; an early
  // return above `useEffect` is `react-hooks/rules-of-hooks`, and it only
  // happens to be harmless here because Next inlines `process.env.NODE_ENV`
  // at build time, so the branch is constant for the life of a bundle. Written
  // this way it is correct for the reason it needs to be correct, rather than
  // by accident of the bundler.
  const isDevelopment = process.env.NODE_ENV === "development"

  useEffect(() => {
    if (!isDevelopment) return

    let attempts = 0
    const maxAttempts = 6 // check for ~3s before first reload

    const hasCss = () => {
      const el = document.getElementById("css-sentinel") as HTMLElement | null
      if (!el) return false
      const color = getComputedStyle(el).backgroundColor
      // Tailwind color should resolve to an rgb(...) value; transparent means CSS didn’t apply
      const colorOk = color && color !== "rgba(0, 0, 0, 0)" && color !== "transparent"

      // Additional heuristic: at least one stylesheet loaded with rules
      const sheets = Array.from(document.styleSheets || []) as CSSStyleSheet[]
      const anyRules = sheets.some((s) => {
        try {
          // Accessing cssRules can throw for cross-origin sheets; guard with try/catch
          return (s.cssRules?.length || 0) > 0
        } catch {
          return false
        }
      })

      return !!colorOk && anyRules
    }

    const reloadWithBackoff = () => {
      try {
        const key = "cssGuardReloadCount"
        const count = Number(sessionStorage.getItem(key) || "0")
        if (count < 2) {
          sessionStorage.setItem(key, String(count + 1))
          const url = new URL(window.location.href)
          url.searchParams.set("cssBust", Date.now().toString())
          console.warn(`[DevCssGuard] CSS missing (attempt ${attempts}). Auto-refreshing with cache bust…`)
          window.location.replace(url.toString())
        } else {
          console.warn("[DevCssGuard] CSS still missing after multiple reloads. Run `npm run dev:clean`.")
        }
      } catch {
        /* no-op */
      }
    }

    const check = () => {
      if (hasCss()) {
        // success: clear reload counter for this tab
        try { sessionStorage.removeItem("cssGuardReloadCount") } catch {}
        return
      }
      if (attempts < maxAttempts) {
        attempts += 1
        setTimeout(check, 500)
      } else {
        reloadWithBackoff()
      }
    }

    const id = window.setTimeout(check, 250)
    return () => window.clearTimeout(id)
  }, [isDevelopment])

  // Production renders nothing at all — unchanged behaviour, moved below the hook.
  if (!isDevelopment) return null

  // Hidden sentinel element styled by Tailwind; if CSS fails, computed color will be transparent
  return (
    <div
      id="css-sentinel"
      className="bg-[#0469f0]"
      style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
    />
  )
}
